import { useReducer, useMemo } from "react";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import { useAxios, useErrMsg } from "hooks";
import {
  formSchema,
  getLicenseStatus,
  getLicenseProvider,
  getLicenseTypeName,
} from "utils";

const initialState = {
  licenseKey: "",
  errorMsg: null,
  activateStep: 0,
  licenseInfo: null,
  licenseList: [],
  displayLicenseList: [],
  licenseFilters: {
    licenseStatusList: [],
    licenseTypeList: [],
    licenseKeyProviderList: [],
  },
  licenseSelectedFilters: {
    status: "all",
    type: "all",
    keyProvider: "all",
  },
  licenseListLoadingStatus: false,
  activateLoadingStatus: false,
  isActivateDialogOpen: false,
};
const ACTION_CLEAN = "clean";
const ACTION_SET_LICENSE_KEY = "action_set_license_key";
const ACTION_SET_ERROR = "action_set_error";
const ACTION_SET_LICENSE_INFO = "set_license_info";
const ACTION_SET_LICENSE_LIST = "license_list";
const ACTION_SET_ACTIVATE_LOADING = "activate_loading";
const ACTION_SET_LICENSE_LIST_LOADING = "license_list_loading";
const ACTION_SET_ACTIVATE_DIALOG_VISIBLE = "activate_dialog_visible";
const ACTION_SET_LICENSE_FILTERS = "set_license_filters";
const ACTION_RESET_LICENSE_FILTERS = "reset_license_filters";

const reducer = (state, action) => {
  const actionType = _.get(action, "type");
  switch (actionType) {
    case ACTION_CLEAN:
      return {
        ...state,
        licenseKey: "",
        errorMsg: null,
        activateStep: 0,
        licenseInfo: null,
      };
    case ACTION_SET_LICENSE_KEY: {
      const licenseKey = _.get(action, "payload.licenseKey", "");
      if (licenseKey.length > 37) {
        return {
          ...state,
          licenseKey: licenseKey.substring(0, 38),
          errMsg: "Invalid license key",
        };
      }
      return {
        ...state,
        licenseKey,
        errMsg: null,
      };
    }
    case ACTION_SET_ERROR: {
      return {
        ...state,
        errMsg: _.get(action, "payload.errMsg", null),
      };
    }
    case ACTION_SET_LICENSE_INFO: {
      return {
        ...state,
        licenseInfo: _.get(action, "payload.licenseInfo", null),
        activateStep: _.get(action, "payload.activateStep", 0),
      };
    }
    case ACTION_SET_LICENSE_LIST: {
      const licenseList = _.get(action, "payload.licenseList", []);
      const selectedFilters = state.licenseSelectedFilters;

      // init display license list
      const displayLicenseList = [...licenseList];

      // get filter values
      const filterOptions = getLicenseFilterOptions(
        licenseList,
        selectedFilters
      );

      return {
        ...state,
        licenseList,
        displayLicenseList,
        licenseFilters: filterOptions,
      };
    }
    case ACTION_SET_ACTIVATE_LOADING: {
      return {
        ...state,
        activateLoadingStatus: _.get(
          action,
          "payload.activateLoadingStatus",
          false
        ),
      };
    }
    case ACTION_SET_LICENSE_LIST_LOADING: {
      return {
        ...state,
        licenseListLoadingStatus: _.get(
          action,
          "payload.licenseListLoadingStatus",
          false
        ),
      };
    }
    case ACTION_SET_ACTIVATE_DIALOG_VISIBLE: {
      return {
        ...state,
        isActivateDialogOpen: _.get(
          action,
          "payload.isActivateDialogOpen",
          false
        ),
        licenseKey: "",
        errMsg: null,
        activateStep: 0,
        licenseInfo: null,
      };
    }
    case ACTION_SET_LICENSE_FILTERS: {
      const { licenseList } = state;
      const filters = _.get(action, "payload.filters", {});
      const selectedFilters = {
        ...state.licenseSelectedFilters,
        ...filters,
      };

      // get display license list
      const displayLicenseList = licenseList.reduce((result, license) => {
        if (
          license.status !== selectedFilters.status &&
          selectedFilters.status !== "all"
        ) {
          return result;
        }
        if (
          license.licenseTypeName !== selectedFilters.type &&
          selectedFilters.type !== "all"
        ) {
          return result;
        }
        if (
          license.keyProvider !== selectedFilters.keyProvider &&
          selectedFilters.keyProvider !== "all"
        ) {
          return result;
        }
        result.push(license);
        return result;
      }, []);

      // get filter values
      const filterOptions = getLicenseFilterOptions(
        licenseList,
        selectedFilters
      );

      // check selected filters
      if (selectedFilters.status !== "all") {
        if (
          !filterOptions.licenseStatusList.find(
            (item) => item === selectedFilters.status
          )
        ) {
          selectedFilters.status = "all";
        }
      }
      if (selectedFilters.type !== "all") {
        if (
          !filterOptions.licenseTypeList.find(
            (item) => item === selectedFilters.type
          )
        ) {
          selectedFilters.type = "all";
        }
      }
      if (selectedFilters.keyProvider !== "all") {
        if (
          !filterOptions.licenseKeyProviderList.find(
            (item) => item === selectedFilters.keyProvider
          )
        ) {
          selectedFilters.keyProvider = "all";
        }
      }

      return {
        ...state,
        licenseSelectedFilters: selectedFilters,
        displayLicenseList,
        licenseFilters: filterOptions,
      };
    }
    case ACTION_RESET_LICENSE_FILTERS: {
      const { licenseList } = state;
      return {
        ...state,
        displayLicenseList: [...licenseList],
        licenseSelectedFilters: {
          ...initialState.licenseSelectedFilters,
        },
        licenseFilters: getLicenseFilterOptions(
          licenseList,
          initialState.licenseSelectedFilters
        ),
      };
    }
    default:
      return state;
  }
};

export const useLicenseActivation = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { isLoading, dataFetcher } = useAxios({ showError: false });
  const getErrMsg = useErrMsg();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const dispatchers = useMemo(() => {
    return {
      clean: function () {
        dispatch({ type: ACTION_CLEAN });
      },
      setLicenseKey: function (licenseKey) {
        dispatch({
          type: ACTION_SET_LICENSE_KEY,
          payload: { licenseKey },
        });
      },
      selectLicenseFilter: function (filterName, filterValue) {
        dispatch({
          type: ACTION_SET_LICENSE_FILTERS,
          payload: {
            filters: {
              [filterName]: filterValue,
            },
          },
        });
      },
      resetLicenseFilters: function () {
        dispatch({
          type: ACTION_RESET_LICENSE_FILTERS,
          payload: {},
        });
      },
      getLicenseList: async function () {
        // trun on loading status
        dispatch({
          type: ACTION_SET_LICENSE_LIST_LOADING,
          payload: {
            licenseListLoadingStatus: true,
          },
        });

        try {
          const result = await dataFetcher("/api/v1/license", "get");
          const licenseList = _.get(result, "data.licenseList", []).map(
            (license) => {
              let status = license.status;
              if (_.get(license, "daysLeft", -1) < 0) {
                status = "EXP";
              }
              return {
                ...license,
                status: getLicenseStatus(status),
                licenseTypeName: getLicenseTypeName(license.licenseTypeId),
                keyProvider: getLicenseProvider(license.licenseKey),
              };
            }
          );

          // set license list
          dispatch({
            type: ACTION_SET_LICENSE_LIST,
            payload: {
              licenseList,
            },
          });
        } catch (err) {
          // display error message
          console.log(err);
          const errCode = _.get(err, "response.data.code");
          enqueueSnackbar(getErrMsg(errCode), {
            variant: "error",
          });
        } finally {
          // turn off loading status
          dispatch({
            type: ACTION_SET_LICENSE_LIST_LOADING,
            payload: {
              licenseListLoadingStatus: false,
            },
          });
        }
      },
      getLicenseInfo: async function (licenseKey) {
        licenseKey = licenseKey.trim();
        const result = formSchema.licenseKey.validate(licenseKey);
        if (_.has(result, "error")) {
          dispatch({
            type: ACTION_SET_ERROR,
            payload: { errMsg: t("Invalid license key") },
          });
          return;
        }

        dispatch({
          type: ACTION_SET_ACTIVATE_LOADING,
          payload: { activateLoadingStatus: true },
        });

        try {
          const result = await dataFetcher(
            `/api/v1/license/${licenseKey}`,
            "GET"
          );
          dispatch({
            type: ACTION_SET_LICENSE_INFO,
            payload: {
              licenseInfo: _.get(result, "data.licenseInfo", {}),
              activateStep: 1,
            },
          });
          return result.data;
        } catch (err) {
          console.error(err);
          // errors are handle in dataFetcher
          const errCode = _.get(err, "response.data.code");
          dispatch({
            type: ACTION_SET_ERROR,
            payload: { errMsg: getErrMsg(errCode) },
          });
        } finally {
          dispatch({
            type: ACTION_SET_ACTIVATE_LOADING,
            payload: { activateLoadingStatus: false },
          });
        }
      },
      activateLicense: async function (licenseKey) {
        licenseKey = licenseKey.trim();
        const result = formSchema.licenseKey.validate(licenseKey);
        if (_.has(result, "error")) {
          dispatch({
            type: ACTION_SET_ERROR,
            payload: { errMsg: t("Invalid license key") },
          });
          return;
        }

        try {
          await dataFetcher("/api/v1/license", "POST", {
            licenseKey,
          });
          enqueueSnackbar(t("Your license was registered successfully."), {
            variant: "success",
          });
          this.setActivateDialogVisible(false);
          await this.getLicenseList();
        } catch (err) {
          console.error(err);
          const errCode = _.get(err, "response.data.code");
          enqueueSnackbar(getErrMsg(errCode), {
            variant: "error",
          });
        }
      },
      setActivateDialogVisible: function (open) {
        dispatch({
          type: ACTION_SET_ACTIVATE_DIALOG_VISIBLE,
          payload: {
            isActivateDialogOpen: open,
          },
        });
      },
    };
  }, [dispatch, dataFetcher, getErrMsg, t, enqueueSnackbar]);
  return { state, isLoading, dispatch, dispatchers };
};

const getLicenseFilterOptions = (licenseList, selectedFilters) => {
  const licenseStatusList = [];
  const licenseTypeList = [];
  const licenseKeyProviderList = [];
  licenseList.forEach((licenseInfo) => {
    if (!licenseStatusList.includes(licenseInfo.status)) {
      licenseStatusList.push(licenseInfo.status);
    }
    if (
      !licenseTypeList.includes(licenseInfo.licenseTypeName) &&
      (selectedFilters.status === "all" ||
        licenseInfo.status === selectedFilters.status)
    ) {
      licenseTypeList.push(licenseInfo.licenseTypeName);
    }
    if (
      !licenseKeyProviderList.includes(licenseInfo.keyProvider) &&
      (selectedFilters.type === "all" ||
        licenseInfo.licenseTypeName === selectedFilters.type)
    ) {
      licenseKeyProviderList.push(licenseInfo.keyProvider);
    }
  });
  return {
    licenseStatusList,
    licenseTypeList,
    licenseKeyProviderList,
  };
};
